/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { includes } from 'lodash';
import { Radio } from '@mui';
import { categoryToInt } from '@utils/filter-utils';
import FilterGroupItem from './filter-group-item';
import '../filter.scss';

const FilterGroup = ({
  category,
  enabled = true,
  filterEnabled,
  filterName,
  getIcon,
  items,
  negateIfEmpty,
  radio,
  setFilters,
  title,
  values
}) => {
  const getFilterNameValue = useCallback(() => {
    if (!category) {
      return filterName;
    }
    // Since we can have more than one category per entity,
    // each category must have their own filter slot.
    return `${filterName}|${category}`;
  }, [category, filterName]);

  const currentValues = useMemo(() => {
    const name = getFilterNameValue();
    // Get the current filters for the specified name:
    const current = values[name];

    if (R.isNil(current) || R.isEmpty(current)) {
      return [];
    }

    // Convert to array:
    const currentAsString = String(current);
    return currentAsString.split(',').map(id => categoryToInt(id));
  }, [getFilterNameValue, values]);

  const getNegatedFilterName = useCallback(() => {
    const name = getFilterNameValue();
    return name.replace(new RegExp('__in$'), '__not_in');
  }, [getFilterNameValue]);

  const onToggleCheckbox = useCallback(id => {
    const name = getFilterNameValue();

    let newIds = null;

    if (includes(currentValues, id)) {
      // If the id is in the list, toggle it off:
      newIds = currentValues.filter(currentId => currentId !== id);
    } else {
      // Else add it to the list:
      newIds = [...currentValues, id];
    }

    const isEmpty = R.isEmpty(newIds);
    if (isEmpty) {
      // If there are no ids, use a value to flag that this
      // filter is off and should be removed.
      newIds = ['clear'];
    }

    const newFilter = {
      [name]: newIds.join(',')  // Convert to string to apply it.
    };

    if (negateIfEmpty) {
      if (isEmpty) {
        const allValues = items.map(item => item.id).join(',');
        newFilter[getNegatedFilterName()] = allValues;
      } else {
        newFilter[getNegatedFilterName()] = '';
      }
    }

    setFilters(newFilter);
  }, [currentValues, getFilterNameValue, getNegatedFilterName, items, negateIfEmpty, setFilters]);

  const isAllSelected = useMemo(() => {
    return items.filter(item => includes(currentValues, item.id)).length === items.length;
  }, [currentValues, items]);

  const onSelectAll = useCallback(() => {
    if (!filterEnabled) {
      return;
    }
    const name = getFilterNameValue();
    const allValues = items.map(item => item.id).join(',');
    if (isAllSelected) {
      const itemIds = items.map(item => item.id);
      const newIds = currentValues.filter(currentId => !includes(itemIds, currentId));
      const newFilter = { [name]: newIds.join(',') };
      if (negateIfEmpty) {
        newFilter[getNegatedFilterName()] = allValues;
      }
      if (category && !newFilter[name]) {
        newFilter[name] = category;
      }
      setFilters(newFilter);
      return;
    }
    const newFilter = { [name]: allValues };
    if (negateIfEmpty) {
      newFilter[getNegatedFilterName()] = '';
    }
    setFilters(newFilter);
  }, [category, currentValues, filterEnabled, getFilterNameValue, getNegatedFilterName, isAllSelected, items, negateIfEmpty, setFilters]);

  const getAllLabel = useCallback(() => {
    if (isAllSelected) {
      return 'UNSELECT ALL';
    }
    return 'SELECT ALL';
  }, [isAllSelected]);

  const onRadioChange = useCallback(event => {
    radio.onCheck(event, event.target.value);
  }, [radio]);

  const renderRadio = () => (
    <div styleName="radio">
      <Radio
        disabled={!filterEnabled}
        checked={radio.checked}
        onChange={onRadioChange}
        value={radio.value}
        inputProps={{ 'aria-label': getAllLabel() }}
        color="primary"
        style={{ padding: 0, marginRight: '0.5rem' }}
      />
    </div>
  );

  return (
    <div styleName="content-filter-group">
      {title && (
        <div styleName="title">
          {radio && renderRadio()}
          <div styleName="label">{title}</div>
          <div
            styleName={`button ${filterEnabled ? '' : 'disabled'}`}
            onClick={onSelectAll}
            onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSelectAll()} // eslint-disable-line react/jsx-no-bind
            role="button"
            tabIndex={filterEnabled ? 0 : -1}
            aria-label={`${getAllLabel()} ${title || ''}`}
            aria-disabled={!filterEnabled}
          >
            {getAllLabel()}
          </div>
        </div>
      )}
      <div styleName="list">
        {items.map(item => (
          <FilterGroupItem
            currentValues={currentValues}
            enabled={enabled}
            filterEnabled={filterEnabled}
            getIcon={getIcon}
            key={item.id}
            item={item}
            onToggleCheckbox={onToggleCheckbox}
          />)
        )}
      </div>
    </div>
  );
};

FilterGroup.propTypes = {
  category: PropTypes.string,
  enabled: PropTypes.bool,
  filterEnabled: PropTypes.bool,
  filterName: PropTypes.string,
  getIcon: PropTypes.func,
  items: PropTypes.array,
  negateIfEmpty: PropTypes.bool,
  radio: PropTypes.object,
  setFilters: PropTypes.func,
  title: PropTypes.string,
  values: PropTypes.object
};

export default memo(FilterGroup);
